import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Table,
} from "react-bootstrap";
import {
  FaRocket,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFileAlt,
  FaDownload,
  FaMagic,
  FaLightbulb,
} from "react-icons/fa";
import Layout from "./Layout";
import axios from "axios";
import "../assets/css/SpAutoSingleTarget.css";

const SpAutoSingleTarget = () => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

  // Targeting Groups Configuration
  const TARGETING_GROUPS = [
    {
      id: "CLOSE_MATCH",
      name: "Close Match",
      description: "Searches closely related to your product",
      icon: "CM",
      color: "#28a745",
    },
    {
      id: "LOOSE_MATCH",
      name: "Loose Match",
      description: "Loosely related searches for your product",
      icon: "LM",
      color: "#17a2b8",
    },
    {
      id: "SUBSTITUTES",
      name: "Substitutes",
      description: "Products customers consider as alternatives",
      icon: "SB",
      color: "#ffc107",
    },
    {
      id: "COMPLEMENTS",
      name: "Complements",
      description: "Products that complement yours",
      icon: "+",
      color: "#6f42c1",
    },
  ];

  // Estados
  const [siteType, setSiteType] = useState("AMAZON_AND_BEYOND");
  const [productsFile, setProductsFile] = useState(null);
  const [selectedTargetingGroup, setSelectedTargetingGroup] =
    useState("CLOSE_MATCH");

  // Campaign config
  const [defaultBid, setDefaultBid] = useState(0.75);
  const [defaultBudget, setDefaultBudget] = useState(10);
  const [campaignState, setCampaignState] = useState("PAUSED");
  const [biddingStrategy, setBiddingStrategy] = useState("SALES_DOWN_ONLY");

  // UI states
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [creationComplete, setCreationComplete] = useState(false);

  // Bulk actions
  const [bulkAction, setBulkAction] = useState("");
  const [bulkBudget, setBulkBudget] = useState("");
  const [bulkBid, setBulkBid] = useState("");

  // Handle file upload
  const handleProductsFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      setProductsFile(file);
      setError(null);
    } else {
      setError("Please select a valid CSV file");
    }
  };

  // Generate campaigns
  const handleGenerateCampaigns = async () => {
    if (!productsFile) {
      setError("You must upload the products file (ASIN, SKU)");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("products_file", productsFile);
      formData.append("site_type", siteType);
      formData.append("default_bid", defaultBid);
      formData.append("default_budget", defaultBudget);
      formData.append("bidding_strategy", biddingStrategy);
      formData.append("targeting_group", selectedTargetingGroup);

      const response = await axios.post(
        `${API_URL}/sp-auto-single/generate`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        const generatedCampaigns = response.data.campaigns || [];

        const campaignsWithSelection = generatedCampaigns.map((campaign) => ({
          ...campaign,
          selected: true,
        }));

        setCampaigns(campaignsWithSelection);

        setResult({
          success: true,
          message: `${generatedCampaigns.length} automatic campaigns generated with ${selectedTargetingGroup}.`,
          count: generatedCampaigns.length,
        });
      } else {
        setError(response.data.error || "Error generating campaigns");
      }
    } catch (err) {
      console.error("Error:", err);
      setError(
        err.response.data.error || err.message || "Error generating campaigns",
      );
    } finally {
      setLoading(false);
    }
  };

  // Create in Amazon
  const handleCreateInAmazon = async () => {
    const selectedCampaigns = campaigns.filter((c) => c.selected);

    if (selectedCampaigns.length === 0) {
      setError("You must select at least one campaign");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const profileId = localStorage.getItem("selectedAccountValue");

      if (!profileId) {
        setError("Please select an account before creating campaigns");
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/sp-auto-single/create-in-amazon`,
        {
          profileId,
          campaigns: selectedCampaigns,
          campaignState,
          siteType,
          biddingStrategy,
          targetingGroup: selectedTargetingGroup,
        },
      );

      if (response.data.success) {
        const amazonResults = response.data.results || [];
        const errors = response.data.errors || [];

        setResult({
          success: true,
          message: response.data.message,
          count: response.data.campaigns_created,
          amazonResults: amazonResults,
          errors: errors,
        });

        const campaignsForTable = amazonResults.map((result) => ({
          "Campaign Name": result.campaignName,
          "Campaign ID": result.campaignId,
          "Targeting Group": result.targetingGroup,
          Status: "Created in Amazon",
        }));

        setCampaigns(campaignsForTable);
        setCreationComplete(true);
      } else {
        setError(response.data.error || "Error creating campaigns in Amazon");
      }
    } catch (err) {
      console.error("Error:", err);
      setError(
        err.response.data.error || err.message || "Error creating campaigns",
      );
    } finally {
      setLoading(false);
    }
  };

  // Selection handlers
  const toggleCampaignSelection = (index) => {
    const updated = [...campaigns];
    updated[index].selected = !updated[index].selected;
    setCampaigns(updated);
  };

  const selectAllCampaigns = () => {
    setCampaigns(campaigns.map((c) => ({ ...c, selected: true })));
  };

  const deselectAllCampaigns = () => {
    setCampaigns(campaigns.map((c) => ({ ...c, selected: false })));
  };

  // Bulk action
  const applyBulkAction = () => {
    if (!bulkAction) return;

    const selectedCount = campaigns.filter((c) => c.selected).length;
    if (selectedCount === 0) {
      setError("Select at least one campaign");
      return;
    }

    const updated = campaigns.map((campaign) => {
      if (!campaign.selected) return campaign;
      const copy = { ...campaign };
      if (bulkAction === "budget" && bulkBudget)
        copy.Budget = parseFloat(bulkBudget);
      if (bulkAction === "bid" && bulkBid) copy.Bid = parseFloat(bulkBid);
      return copy;
    });

    setCampaigns(updated);
    setBulkAction("");
    setBulkBudget("");
    setBulkBid("");
  };

  // Download CSV
  const handleDownloadCampaigns = () => {
    if (campaigns.length === 0) return;
    const headers = Object.keys(campaigns[0]).filter((h) => h !== "selected");
    const csvContent = [
      headers.join(","),
      ...campaigns.map((c) => headers.map((h) => c[h]).join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `sp_auto_single_${selectedTargetingGroup}_${Date.now()}.csv`;
    link.click();
  };

  // Reset
  const handleReset = () => {
    setProductsFile(null);
    setCampaigns([]);
    setResult(null);
    setError(null);
    setCreationComplete(false);
  };

  // Get selected group info
  const getSelectedGroupInfo = () => {
    return TARGETING_GROUPS.find((g) => g.id === selectedTargetingGroup);
  };

  return (
    <Layout>
      <Container fluid className="sp-auto-single-page py-4">
        <Row className="mb-4">
          <Col>
            <h2 className="text-white mb-3">
              <FaMagic className="me-2" />
              Auto Single Target - Sponsored Products
            </h2>
            <p className="text-muted">
              Automatic campaigns with ONE active targeting group
            </p>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <FaExclamationTriangle className="me-2" />
            {error}
          </Alert>
        )}

        {result?.success && !creationComplete && (
          <Alert variant="success" onClose={() => setResult(null)} dismissible>
            <FaCheckCircle className="me-2" />
            {result.message}
          </Alert>
        )}

        {/* Configuration */}
        {!result?.amazonResults && (
          <>
            {/* Info Banner */}
            <Alert variant="primary" className="mb-4">
              <FaMagic className="me-2" />
              <strong>Automatic Targeting:</strong> Only ONE targeting group
              active per campaign
            </Alert>

            {/* SITE Selection */}
            <div className="config-section-card mb-4">
              <h5 className="mb-3">
                <Badge bg="primary" className="me-2">
                  1
                </Badge>
                Select SITE
              </h5>
              <Form.Select
                value={siteType}
                onChange={(e) => setSiteType(e.target.value)}
                className="site-selector"
              >
                <option value="AMAZON_AND_BEYOND">Amazon and Beyond</option>
                <option value="AMAZON_BUSINESS">Amazon Business</option>
              </Form.Select>
            </div>

            {/* Upload */}
            <div className="upload-grid">
              <div className={`upload-card-new ${productsFile ? "has-file" : ""}`}
              >
                <div className="card-required-badge">Required</div>
                <Badge bg="primary" className="step-badge">
                  2
                </Badge>
                <div className="upload-icon">
                  <FaFileAlt />
                </div>
                <div className="upload-card-title">Products CSV</div>
                <div className="upload-card-desc">Columns: ASIN, SKU</div>
                <label className="w-100 mt-auto">
                  <div className="upload-btn">
                    {productsFile ? "Change File" : "Select File"}
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleProductsFileChange}
                    hidden
                  />
                </label>
                {productsFile && (
                  <small className="text-success mt-2">
                    {productsFile.name}
                  </small>
                )}
              </div>
            </div>

            {/* Targeting Group Selection */}
            <div className="config-section-card mt-4">
              <h5 className="mb-3">
                <Badge bg="primary" className="me-2">
                  3
                </Badge>
                Select Targeting Group (Only 1)
              </h5>
              <p className="text-muted small mb-3">
                Select the only targeting group that will be ACTIVE. The
                rest will be disabled.
              </p>

              <div className="targeting-groups-grid">
                {TARGETING_GROUPS.map((group) => (
                  <div
                    key={group.id}
                    className={`targeting-group-card ${selectedTargetingGroup === group.id ? "selected" : ""}`}
                    onClick={() => setSelectedTargetingGroup(group.id)}
                    style={{ "--group-color": group.color }}
                  >
                    <div className="group-icon">{group.icon}</div>
                    <div className="group-name">{group.name}</div>
                    <div className="group-description">{group.description}</div>
                    {selectedTargetingGroup === group.id && (
                      <div className="group-selected-badge">
                        <FaCheckCircle /> ACTIVE
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Campaign Config */}
            <div className="config-section-card mt-4">
              <h5 className="mb-3">
                <Badge bg="primary" className="me-2">
                  4
                </Badge>
                Configuration
              </h5>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">Bid ($)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0.02"
                      value={defaultBid}
                      onChange={(e) =>
                        setDefaultBid(parseFloat(e.target.value))
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Daily Budget ($)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      step="1"
                      min="1"
                      value={defaultBudget}
                      onChange={(e) =>
                        setDefaultBudget(parseFloat(e.target.value))
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Bidding Strategy
                    </Form.Label>
                    <Form.Select
                      value={biddingStrategy}
                      onChange={(e) => setBiddingStrategy(e.target.value)}
                    >
                      <option value="SALES_DOWN_ONLY">
                        Dynamic - Down Only
                      </option>
                      <option value="SALES_UP_AND_DOWN">
                        Dynamic - Up and Down
                      </option>
                      <option value="FIXED_BID">Fixed Bids</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Targeting Type
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value="AUTOMATIC"
                      disabled
                      className="bg-dark text-success fw-bold"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Generate Button */}
            <div className="generate-section mt-4">
              <button className={`btn-generate-campaigns ${!loading && productsFile ? "ready" : ""}`}
                onClick={handleGenerateCampaigns}
                disabled={loading || !productsFile}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FaMagic />
                    Generate Auto Campaigns ({getSelectedGroupInfo().name})
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {/* Results Table */}
        {result && campaigns.length > 0 && (
          <Row>
            <Col>
              <Card className="config-section-card border-0 p-0 overflow-hidden">
                <Card.Header className="bg-dark text-white p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>
                      <FaMagic className="me-2" />
                      Auto Single Campaigns ({campaigns.length})
                    </h5>
                  </div>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex gap-2 align-items-center">
                      <Form.Select
                        size="sm"
                        value={campaignState}
                        onChange={(e) => setCampaignState(e.target.value)}
                        style={{ width: "auto" }}
                        disabled={creationComplete}
                      >
                        <option value="ENABLED">Enabled</option>
                        <option value="PAUSED">Paused</option>
                      </Form.Select>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={handleDownloadCampaigns}
                      >
                        <FaDownload className="me-1" /> CSV
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleReset}
                      >
                        <FaRocket className="me-1" /> New
                      </Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body
                  className="p-0 table-responsive bg-dark" style={{ maxHeight : "500px", overflowY: "auto" }}
                >
                  <Table
                    hover
                    variant="dark"
                    className="align-middle mb-0"
                    size="sm"
                  >
                    <thead className="text-success sticky-top bg-dark">
                      <tr>
                        <th width="40">
                          <Form.Check
                            type="checkbox"
                            checked={campaigns.every((c) => c.selected)}
                            onChange={(e) =>
                              e.target.checked ? selectAllCampaigns() : deselectAllCampaigns()
                            }
                          />
                        </th>
                        <th>#</th>
                        <th>Campaign Name</th>
                        <th>ASIN</th>
                        <th>Targeting Group</th>
                        <th>Bid</th>
                        <th>Budget</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((c, idx) => (
                        <tr key={idx}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={c.selected || false}
                              onChange={() => toggleCampaignSelection(idx)}
                            />
                          </td>
                          <td>{idx + 1}</td>
                          <td
                            className="text-truncate" style={{ maxWidth : "250px" }}
                          >
                            {c["Campaign Name"]}
                          </td>
                          <td className="text-info">{c.ASIN}</td>
                          <td>
                            <Badge bg="success">{c["Targeting Group"]}</Badge>
                          </td>
                          <td>${c.Bid}</td>
                          <td>${c.Budget}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
                <Card.Footer className="bg-dark border-top border-secondary d-flex justify-content-between p-3">
                  <Button variant="secondary" onClick={handleReset}>
                    Back
                  </Button>
                  <div className="d-flex gap-2 align-items-center">
                    <span className="text-muted small">
                      Selected:{" "}
                      {campaigns.filter((c) => c.selected).length}
                    </span>
                    <Button
                      variant="success"
                      onClick={handleCreateInAmazon}
                      disabled={
                        loading ||
                        creationComplete ||
                        campaigns.filter((c) => c.selected).length === 0
                      }
                    >
                      {loading ? (
                        <Spinner size="sm" />
                      ) : creationComplete ? (
                        <>
                          <FaCheckCircle /> Created
                        </>
                      ) : (
                        "Create in Amazon"
                      )}
                    </Button>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          </Row>
        )}

        {/* Requirements */}
        {!result && (
          <div className="requirements-section mt-4">
            <span className="requirements-title">Auto Single Target</span>
            <div className="req-item">
              <strong>Products CSV:</strong> ASIN, SKU
            </div>
            <div className="req-item">
              <strong>Targeting:</strong> AUTOMATIC (fixed)
            </div>
            <div className="req-item">
              <strong>Targeting Group:</strong> Only 1 active at a time
            </div>
            <div className="req-item">
              <strong>Options:</strong> Close Match, Loose Match, Substitutes,
              Complements
            </div>
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default SpAutoSingleTarget;

